import React, { useEffect, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router';

import { getCustomStorageSettings, postCustomStorageSettings } from '../../../api';
import { useCurrentProject } from '../../../contexts/settings';
import { useNotifications } from '../../../hooks/useNotifications';
import { usePaths } from '../../../routes/routesHelper';
import { RouteParams } from '../../../routes/types';
import { HorizontalCenterColumnBlock, ShortSettingsBlock } from '../../../styled';
import { FormFooter, HeaderTitle, Preloader, RadioGroup } from '../../ui';
import CustomStorageFields from './CustomStorageFields';
import FirebaseFields from './FirebaseFields';
import PlayfabFields from './PlayfabFields';
import { DataStorageFormValues, UserStorageTypes } from './settings';
import { extractKeyValuePairs, transformArray } from './utils';
import { UserDataStorageValidator } from './validator';

type TransformedObject = {
  [key: string]: string | number;
};

const DataStorageForm = ({ match, history }: RouteComponentProps): JSX.Element => {
  const { navigationPath } = usePaths();
  const intl = useIntl();
  const { projectId } = match.params as RouteParams;
  const { currentUpdateFetching, updateCurrentProject, currentProject } = useCurrentProject();

  const [submitError, setSubmitError] = useState<boolean>(false);
  const [fetching, setFetching] = useState<boolean>(false);
  const [customStorageValues, setCustomStorageVales] = useState<TransformedObject>({});
  const { showErrorNotify, showSuccessNotify } = useNotifications();

  useEffect(() => {
    setFetching(true);
    getCustomStorageSettings(projectId)
      .then((res) => {
        setCustomStorageVales(transformArray(res));
      })
      .catch((err) => false)
      .finally(() => setFetching(false));
  }, [currentProject]);

  const onSubmit = async (values: DataStorageFormValues) => {
    if (!currentProject) {
      return;
    }

    const {
      users_storage,
      playfab_title_id,
      firebase_apikey,
      add_social_access_token,
      refresh_token_url
    } = values;

    const updateCurrentProjectValue: DataStorageFormValues = {
      users_storage,
      playfab_title_id,
      firebase_apikey,
      add_social_access_token,
      refresh_token_url
    };

    const customStorageValuesFinal = extractKeyValuePairs(values);

    try {
      setSubmitError(false);
      if (users_storage === UserStorageTypes.custom) {
        await postCustomStorageSettings(projectId, customStorageValuesFinal);
        await updateCurrentProject({ ...currentProject, ...updateCurrentProjectValue });
      } else {
        await updateCurrentProject({ ...currentProject, ...updateCurrentProjectValue });
      }
      showSuccessNotify();
    } catch (err) {
      showErrorNotify(err);
      setSubmitError(true);
    }
  };

  const backToNavigation = () =>
    currentProject?.id && history.push({ pathname: navigationPath(currentProject.id) });

  if (fetching) {
    return <Preloader />;
  }

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={{
        ...customStorageValues,
        users_storage: currentProject?.users_storage,
        playfab_title_id: currentProject?.playfab_title_id,
        firebase_apikey: currentProject?.firebase_apikey,
        add_social_access_token: currentProject?.add_social_access_token
      }}
      validate={UserDataStorageValidator}>
      {({ handleSubmit, errors, pristine, values: { users_storage } }) => {
        return (
          <div
            style={{
              width: '100%'
            }}>
            <HorizontalCenterColumnBlock>
              <HeaderTitle
                title={intl.formatMessage({ id: 'user-data-base.settings.title' })}
                subtitle={intl.formatMessage({ id: 'user-data-base.settings.subtitle' })}
              />

              <ShortSettingsBlock>
                <Field name="users_storage" type="radio">
                  {(props) => (
                    <>
                      <RadioGroup
                        name={props.input.name}
                        onChange={props.input.onChange}
                        defaultValue={currentProject?.users_storage || 'xsolla'}
                        options={Object.values(UserStorageTypes).map((item) => ({
                          label:
                            intl.messages[`user-data-base.settings.user-storage.${item}.label`],
                          description:
                            intl.messages[
                              `user-data-base.settings.user-storage.${item}.description`
                            ],
                          radioValue: item
                        }))}
                      />
                      {users_storage === UserStorageTypes.custom && (
                        <CustomStorageFields errors={errors} />
                      )}
                      {users_storage === UserStorageTypes.playfab && (
                        <PlayfabFields errors={errors} />
                      )}
                      {users_storage === UserStorageTypes.firebase && (
                        <FirebaseFields errors={errors} />
                      )}
                    </>
                  )}
                </Field>
              </ShortSettingsBlock>
            </HorizontalCenterColumnBlock>

            <FormFooter
              onSubmit={handleSubmit}
              onCancel={backToNavigation}
              fetching={currentUpdateFetching}
              disabled={pristine}
              error={submitError}
            />
          </div>
        );
      }}
    </Form>
  );
};

export default withRouter(DataStorageForm);
